// client/src/utils/resolveImageUrl.js

const API_ORIGIN =
  import.meta.env.VITE_API_URL?.replace(/\/api\/?$/, "") ||
  import.meta.env.VITE_SERVER_URL ||
  "";

const CDN_ORIGIN = import.meta.env.VITE_CDN_URL || "https://cdn.pearlvoyages.com";
const LIVE_SITE_HOSTS = new Set(["pearlvoyages.com", "www.pearlvoyages.com"]);
const LEGACY_IMAGE_FALLBACKS = [
  {
    pattern: /\/uploads\/gorillatrekkingexperiences\.jpg$/i,
    fallback: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1800&q=80",
  },
  {
    pattern: /\/home-page\/hero-image-lion\.webp$/i,
    fallback: "https://cdn.pearlvoyages.com/home-page/hero-image-lion.webp",
  },
];

const isLocalOrigin = (origin = "") => /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?$/i.test(String(origin || ""));

const buildCdnUrlFromKey = (key = "") =>
  `${CDN_ORIGIN.replace(/\/$/, "")}/${String(key || "")
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/")}`;

const rewriteAbsoluteLiveSiteUrl = (url) => {
  try {
    const parsed = new URL(url);
    if (!LIVE_SITE_HOSTS.has(parsed.hostname.toLowerCase())) {
      return url;
    }

    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;

    if (parsed.pathname.startsWith("/uploads/packages/")) {
      const filename = parsed.pathname.replace("/uploads/packages/", "");
      return buildCdnUrlFromKey(`tour-packages/${filename}`);
    }

    if (parsed.pathname.startsWith("/uploads/")) {
      return buildCdnUrlFromKey(parsed.pathname.replace(/^\/uploads\//, ""));
    }

    return `${CDN_ORIGIN.replace(/\/$/, "")}${path}`;
  } catch {
    return url;
  }
};

const resolveImageUrl = (input) => {
  if (!input) return "";

  const url = String(input).trim();

  const legacyMatch = LEGACY_IMAGE_FALLBACKS.find(({ pattern }) => pattern.test(url));
  if (legacyMatch) {
    return legacyMatch.fallback;
  }

  // Already absolute
  if (/^https?:\/\//i.test(url)) {
    return rewriteAbsoluteLiveSiteUrl(url);
  }

  // Canonical tour-package keys should resolve through the CDN.
  if (url.startsWith("tour-packages/") || url.startsWith("/tour-packages/")) {
    return buildCdnUrlFromKey(url.replace(/^\/+/, ""));
  }

  // Legacy local package path that was previously stored by the admin.
  if (url.startsWith("/uploads/packages/")) {
    const filename = url.replace("/uploads/packages/", "");
    return buildCdnUrlFromKey(`tour-packages/${filename}`);
  }

  // Old local uploads path
  if (url.startsWith("/uploads/")) {
    if (API_ORIGIN && isLocalOrigin(API_ORIGIN)) {
      return `${API_ORIGIN}${url}`;
    }

    if (!API_ORIGIN || API_ORIGIN.startsWith("/")) {
      return url;
    }

    return buildCdnUrlFromKey(url.replace(/^\/uploads\//, ""));
  }

  // CDN-relative product path like /products/x.jpg
  if (url.startsWith("/products/")) {
    return `${CDN_ORIGIN}${url}`;
  }

  // Bare filename fallback
  if (!url.startsWith("/")) {
    return `${CDN_ORIGIN}/products/${url}`;
  }

  return url;
};

export default resolveImageUrl;
